import React,{useRef, useState } from "react";
import { RCCButton, RCCDropDownItem, RCCGrid, RCCHookToast, RCCIcon, RCCServicesApi, RCCTooltip } from "react-common-components";
import Accordion from "src/components/Accordion";
import OptionsColumn from "src/components/DefaultColumnsList/Options";
import List from "src/components/List";
import Loader from "src/components/Loader";
import Title from "src/components/Title";
import { useAuth } from "src/context/Auth/context";
import { useConfirmModal } from "src/context/ConfirmModal/context";
import { useTranslation } from "src/context/Translations/context";
import useEffectSkipInitialRender from "src/hooks/useEffectSkipInitialRender";
import { standardErrorMessages } from "src/utils/errors";
import { IParameterGroup, IPatba002List, IRefParametersModal } from "./interface";
import ModalParameter from "./modalParameter";

const ParameterGroup : React.FC<IParameterGroup>  = ({
	group,
	filters,
	deleteAction,
	updateAction 
}) => {
	const controllerUrl = "Parameters/"; 

	const { translate} = useTranslation();
	const { addToast} = RCCHookToast();
	const {superUser} = useAuth();
	const {confirm} = useConfirmModal();
	const [expandedAccordion, setExpandedAccordion] = useState(false);
	const [loading, setLoading] = useState(false);
	const [parameters, setParameters] = useState<IPatba002List[]>([]);    
	const modalparameters = useRef<IRefParametersModal>(null);

	const RequestParameters = async() =>
	{
		try {
			setLoading(true);
			const dataController = await RCCServicesApi.getRequest({
				method: "Parameters/ListParameters",
				urlParameters:{...filters,groupId:group.paa001Id}	
			});

			setParameters(dataController.data);
            
			setLoading(false);

		} catch (error) {
			standardErrorMessages(error, translate("Erro ao buscar grupos de parâmetros"), addToast);
			setLoading(false);
		}
	};

	const deleteParameter = (parameterId: number) => {
		confirm(
			translate("Exclusão de Parâmetro"), 
			`${translate("Deseja realmente excluir o parâmetro")}?`,
			async () => {
				await RCCServicesApi.deleteRequest({
					method: `${controllerUrl}DeleteParameter`,
					urlParameters: {
						parameterId
					}
				});

				addToast({
					color: "green",
					description: translate("Parâmetro excluído com sucesso"),
					title: translate("Sucesso")
				});

				setParameters((prevState) => prevState.filter((x) => x.paa002Id!==parameterId));

			},
			(err) => {
				standardErrorMessages(err, translate("Erro ao excluir parâmetro"), addToast);
			},
			true
		);
	};

	const colOptions = (row: any) => 
		superUser && 
			<OptionsColumn>
				<RCCDropDownItem
					selfEnclose
					onClick={() => modalparameters.current?.changeParameter(row.paa002Id,() => RequestParameters()) }
				>
					<RCCIcon
						name="pencil" 
					/>
					{translate("Alterar")}
				</RCCDropDownItem>
				<RCCDropDownItem
					selfEnclose
					onClick={() => { deleteParameter(row.paa002Id);}}
				>
					<RCCIcon
						name="trash"
					/>
					{translate("Excluir")}
				</RCCDropDownItem>
			</OptionsColumn>
	;

	const handleAccordion = (expanded:boolean) => {
		setExpandedAccordion(expanded);

		if(!expanded)
			return;

		RequestParameters();
	};

	useEffectSkipInitialRender(() => {
		if(group)
			RequestParameters();
	},[filters]);

	return (	
		<>
			<Accordion 
				expanded={expandedAccordion}
				onChange={(_, expanded) => handleAccordion(expanded)}
				TransitionProps={{ unmountOnExit: true }} 
				header={
					<RCCGrid container 
						spacing={2} 
						wrap="nowrap"
					>
						<RCCGrid container item 
							justifyContent="space-between" 
							alignContent="center"
						>
							<RCCGrid item xs={9}> 
								<Title bold color="purple">{`${group.paa001Grupo} - ${group.paa001Descricao}`}</Title>
							</RCCGrid>
							<RCCGrid item container justifyContent="flex-end" wrap="nowrap" xs={3}>
								<RCCTooltip
									text={translate("Inserir parâmetro")}
									color="purple"
								>
									<RCCButton
										icon={{name: "plus"}}
										outlined
										color="purple"
										layout="isRounded"
										size="xs"
										onClick={(e) => { e.stopPropagation(); modalparameters.current?.addParameter(group.paa001Id,() => RequestParameters());}}
									/>
								</RCCTooltip>
								<RCCTooltip
									text={translate("Alterar grupo")}
									color="grey"
								>
									<RCCButton
										icon={{name: "pencil"}}
										outlined
										color="grey"
										layout="isRounded"
										size="xs"
										onClick={(e) => { e.stopPropagation(); updateAction(group);}}
									/>
								</RCCTooltip>   
								<RCCTooltip
									text={translate("Excluir grupo")}
									color="red"
								>
									<RCCButton
										icon={{name: "trash"}}
										outlined
										color="red"
										layout="isRounded"
										size="xs"
										onClick={(e) => { e.stopPropagation(); deleteAction(group.paa001Id,group.paa001Grupo);}}
									/>
								</RCCTooltip>   
							</RCCGrid>
						</RCCGrid>
					</RCCGrid>
				}
			>
				{loading ? 
					<Loader/>
					:
					<RCCGrid container>
						<List
							data={parameters}
							columns={[
								{
									idColumn:"description",
									nameColumn:"",
									cellRenderer:
										(parameter:any) => <Title color="purple"> {parameter.paa002Parametro} - {parameter.paa002Descricao}</Title>,
									xs:10,
									justify:"left"
								},
								{
									idColumn:"options",
									nameColumn:"",
									cellRenderer:colOptions,
									xs:2,
									justify:"right"
								}
							]} 
						/>
					</RCCGrid>
				}
			</Accordion>

			<ModalParameter
				ref={modalparameters}
			/>
		</>
	);

};

export default ParameterGroup;
